'use client';

import React, { useEffect, useRef, useState } from 'react';
import {
  Shield,
  Smartphone,
  BarChart3,
  Heart,
  Wallet,
  Layers,
  LucideIcon,
} from 'lucide-react';

interface Service {
  id: string;
  icon: LucideIcon;
  title: string;
  description: string;
  iconBg: string;
  iconColor: string;
}

const services: Service[] = [
  {
    id: 'batch-payments',
    icon: Layers,
    title: 'Batch Payment System',
    description:
      'Process hundreds of payments in a single batch operation. Automate disbursements with multi-level approval workflows and real-time tracking.',
    iconBg: 'bg-primary/10',
    iconColor: 'text-primary',
  },
  {
    id: 'event-pass',
    icon: Heart,
    title: 'Ceremony & Contribution Management',
    description:
      'Manage ceremonies, send digital invitations, and collect contributions automatically. Track RSVPs and monitor contribution progress in real-time.',
    iconBg: 'bg-secondary/10',
    iconColor: 'text-secondary',
  },
  {
    id: 'digital-wallet',
    icon: Wallet,
    title: 'Digital Wallet Solutions',
    description:
      'Centralized wallet system for managing organizational funds. Monitor balances, track spending, and maintain financial control across all operations.',
    iconBg: 'bg-accent/10',
    iconColor: 'text-accent',
  },
  {
    id: 'analytics',
    icon: BarChart3,
    title: 'Analytics & Reporting',
    description:
      'Track operations, approval rates, and performance metrics in real-time. Make data-driven decisions with comprehensive dashboards and reports.',
    iconBg: 'bg-success/10',
    iconColor: 'text-success',
  },
  {
    id: 'compliance',
    icon: Shield,
    title: 'Compliance & Security',
    description:
      'Automated document verification and KYC checks. Maintain audit trails and ensure regulatory compliance for every transaction.',
    iconBg: 'bg-info/10',
    iconColor: 'text-info',
  },
  {
    id: 'mobile-integration',
    icon: Smartphone,
    title: 'Mobile Integration',
    description:
      'Seamlessly connect with M-Pesa, Airtel Money, and other mobile platforms. Enable instant transactions across multiple providers.',
    iconBg: 'bg-warning/10',
    iconColor: 'text-warning',
  },
];

export default function ServicesOverview() {
  const [visibleCards, setVisibleCards] = useState<Set<string>>(new Set());
  const cardRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const id = entry.target.getAttribute('data-service-id');
            if (id) {
              setVisibleCards((prev) => new Set(prev).add(id));
            }
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px',
      }
    );

    cardRefs.current.forEach((element) => {
      observer.observe(element);
    });

    return () => observer.disconnect();
  }, []);

  const setCardRef = (id: string, element: HTMLDivElement | null) => {
    if (element) {
      cardRefs.current.set(id, element);
    }
  };

  return (
    <section id="features" className="py-20 px-4 bg-background">
      <div className="container mx-auto max-w-7xl">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
            Our Services
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Comprehensive digital solutions tailored for African businesses
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service) => {
            const Icon = service.icon;
            const isVisible = visibleCards.has(service.id);

            return (
              <div
                key={service.id}
                ref={(el) => setCardRef(service.id, el)}
                data-service-id={service.id}
                className={`group relative bg-card border border-border rounded-xl p-8 transition-all duration-500 hover:shadow-card-hover hover:-translate-y-1 ${
                  isVisible
                    ? 'opacity-100 translate-y-0'
                    : 'opacity-0 translate-y-8'
                }`}
              >
                <div
                  className={`w-14 h-14 rounded-xl flex items-center justify-center mb-6 ${service.iconBg} transition-transform duration-300 group-hover:scale-110`}
                >
                  <Icon size={28} className={service.iconColor} />
                </div>

                <h3 className="text-xl font-bold text-foreground mb-3">
                  {service.title}
                </h3>

                <p className="text-muted-foreground leading-relaxed">
                  {service.description}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
